import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon1 from "../icons/Icon";

type DropDownItem = {
  value: string | number;
  label: string;
  extra_label?: string;
  type?: string;
};

type DropDownGPT03Props = {
  disabled?: boolean;
  label: string;
  items: DropDownItem[];
  value?: string | number;
  onChange: (item: DropDownItem) => void;
  className?: string;
  mandatory?: boolean;
  grow_on_focus?: boolean;
};

function DropDownGPT03({
  disabled = false,
  label,
  items,
  value = "",
  onChange,
  className,
  mandatory = false,
  grow_on_focus = false,
}: DropDownGPT03Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpwards, setOpenUpwards] = useState<boolean | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleSelect = (item: any) => {
    onChange(item);
    setIsOpen(false);
  };

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      // If there's more space above than below, open upwards
      const shouldOpenUpwards = spaceAbove > spaceBelow && spaceBelow < 200;
      setOpenUpwards(shouldOpenUpwards);

      // Set position relative to viewport
      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  let has_value = value !== "" && value !== null && value !== undefined;
  let disabled_classname =
    " focus:outline-none focus:ring-transparent focus:border-neutral-300 focus:border-1 text-shadow_25 bg-neutral-100 border-neutral-300";

  // Portal dropdown content
  const dropdownContent = isOpen ? (
    <ul
      style={{
        position: "fixed",
        top: openUpwards ? dropdownPosition.top - 240 : dropdownPosition.top, // Adjust height as needed
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999, // Very high z-index to ensure it's on top
        maxHeight: "240px",
      }}
      className="flex flex-col gap-1 bg-white shadow-lg rounded-md p-2 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
    >
      {items
        .filter((item) => item?.value !== undefined)
        .map((item) => (
          <li
            key={item.value}
            className={`${
              item.type === 'header' 
                ? "cursor-default select-none relative py-2 pl-3 pr-9 text-indigo-700 font-bold text-sm bg-indigo-50 border-l-4 border-indigo-400 uppercase tracking-wide"
                : `cursor-pointer select-none relative rounded-lg py-2 pl-6 pr-9 ${
                    has_value && value === item.value
                      ? "text-indigo-500 bg-blue-50 font-semibold"
                      : "text-gray-900 hover:bg-indigo-10"
                  }`
            }`}
            onMouseDown={(e) => {
              e.preventDefault();
              if (item.type !== 'header') {
                handleSelect(item);
              }
            }}
          >
            <div className="flex items-center">
              <span
                className={`block truncate ${
                  has_value && value === item.value ? "font-regular" : ""
                }`}
              >
                {item.label}
                {item.extra_label && (
                  <span
                    className={`${
                      item.type
                        ? item.type === "particulier"
                          ? "text-purple-400 bg-purple-50"
                          : item.type === "constructor"
                          ? "text-indigo-400 bg-indigo-50"
                          : item.type === "expert"
                          ? "text-green-400 bg-green-50"
                          : "text-blue-400 bg-blue-50"
                        : "text-blue-400 bg-blue-50"
                    } font-light rounded-full px-1 text-h4 ml-1`}
                  >
                    {item.extra_label}
                  </span>
                )}
              </span>
            </div>
            {has_value && value === item.value ? (
              <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                <svg
                  className="h-5 w-5 text-indigo-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 5.707 8.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            ) : null}
          </li>
        ))}
      {(items || []).length === 0 && (
        <li className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
          Aucune option disponible
        </li>
      )}
    </ul>
  ) : null;

  return (
    <div
      className={`relative select-none outline-none border-0 ${className}`}
      style={{ width: grow_on_focus ? (isOpen ? "" : "150px") : "" }}
    >
      <div
        ref={dropdownRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onBlur={() => setIsOpen(false)}
        tabIndex={0}
        className={`relative w-full px-2 outline-none ${
          label ? " h-11" : "h-8"
        } border flex ${
          has_value && label ? "items-end py-1" : "items-center py-1"
        } flex-row justify-between rounded-xl shadow-sm ${
          disabled
            ? disabled_classname
            : mandatory && !has_value && !isOpen
            ? "border-red-300 cursor-pointer border-2 outline-1 outline-red-100"
            : "focus:outline-1 focus:outline-blue-100 focus:border-blue-300 focus:border-2 cursor-pointer bg-transparent border"
        } sm:text-sm`}
      >
        {mandatory && !has_value && !isOpen && (
          <div className="absolute top-0 justify-start h-full items-start flex py-0 flex-row right-8 text-white text-h6">
            <div className="bg-red-300 flex flex-row items-end rounded-b-lg px-1 h-5 pb-0.5">
              Champ obligatoire
            </div>
          </div>
        )}
        {has_value && label && (
          <div
            className={`absolute top-0 text-h6 ${
              isOpen ? "text-blue-400" : "text-gray-300"
            }`}
          >
            {label}
          </div>
        )}
        {!isOpen && (
          <div className="absolute top-1/3 right-2 px-1">
            <Icon1
              icon_name="down"
              size={15}
              fill={mandatory && !has_value && !isOpen ? "red" : "grey"}
            />
          </div>
        )}
        {isOpen && (
          <div className="absolute top-1/3 right-3">
            <Icon1 icon_name="up" size={15} fill="grey" />
          </div>
        )}
        <div className="flex flex-row items-center justify-between w-full text-nowrap overflow-hidden">
          {has_value && (
            <div className="flex flex-row justify-between items-center w-full">
              <span>
                {
                  items
                    .filter((item) => item?.value !== undefined)
                    .find((item) => item.value === value)?.label
                }
              </span>
            </div>
          )}
          {!has_value && (
            <div className="flex flex-row justify-between items-center w-full">
              <div className="text-shadow_50">{label}</div>
            </div>
          )}
        </div>
      </div>

      {/* Render dropdown in portal to escape parent constraints */}
      {typeof window !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
}

export default DropDownGPT03;
